import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import Tooltip from '@/components/common/Tooltip';
import StepTimeSelect from '@/components/general/StepTimeSelect';
import FormatNumber from '@/components/react-number-format';
import { BOOK_DAY_IN_ADVANCE, DISCOUNT_RATE, FORM_TYPE, MAX_PRICE_SERVICE, NUMBER_OF_VOUCHER, ROUTES, USAGE_LIMIT_VOUCHER } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { BookMarkedIcon, CircleMinus, CirclePlus, MessageCircleQuestion, ShieldQuestionIcon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const DISCOUNT_TYPE = {
    PERCENT: 'percent',
    FIXED: 'fixed'
};

interface FormPromotionProps {
    typeForm: string
}

const validateSchema = yup.object().shape({
    shopId: yup
        .string()
        .nullable()
        .when('isAllShop', {
            is: false,
            then: schema => schema.required('form_error_validate.required'),
            otherwise: schema => schema.nullable()
        }),
    shopName: yup
        .string()
        .when('isAllShop', {
            is: false,
            then: schema => schema.required('form_error_validate.required'),
            otherwise: schema => schema.nullable()
        }),
    isAllShop: yup
        .boolean(),
    nameVn: yup
        .string(),
    nameEn: yup
        .string(),
    promotionCode: yup
        .string()
        .required('form_error_validate.required')
        .matches(/^[A-Z0-9]+$/, 'form_error_validate.validate_promotion_code'),
    startTime: yup
        .date()
        .typeError('form_error_validate.invalid_date')
        .nullable()
        .test('start-or-end-required', 'form_error_validate.required', function (value) {
            const { endTime } = this.parent;
            return !!value || !!endTime;
        }),

    endTime: yup
        .date()
        .typeError('form_error_validate.invalid_date')
        .nullable()
        .test('start-or-end-required', 'form_error_validate.required', function (value) {
            const { startTime } = this.parent;
            return !!value || !!startTime;
        }),
    quantity: yup
        .number()
        .min(1, 'form_error_validate.min_1')
        .required('form_error_validate.required'),
    perUserLimit: yup
        .number()
        .min(1, 'form_error_validate.min_1')
        .required('form_error_validate.required'),
    descriptionVn: yup
        .string(),
    descriptionEn: yup
        .string(),
    status: yup
        .boolean()
        .required('form_error_validate.required'),
    isFirstOrder: yup
        .boolean(),
    isFirstReview: yup
        .boolean(),
    isFirstTimeUser: yup
        .boolean(),
    isTimeRange: yup
        .boolean(),
    promotionTimeRanges: yup
        .array()
        .of(
            yup.object().shape({
                fromTime: yup.string().required('form_error_validate.required'),
                toTime: yup.string().required('form_error_validate.required')
            })
        ),
    isProvince: yup
        .boolean(),
    provinceIds: yup
        .array()
        .of(
            yup.object().shape({
                provinceName: yup.string(),
                provinceCode: yup.string()
            })
        )
        .min(1, 'form_error_validate.required'),
    isMinTotal: yup
        .boolean(),
    minTotal: yup
        .string()
        .when('isMinTotal', {
            is: true,
            then: schema => schema.required('form_error_validate.required'),
            otherwise: schema => schema.notRequired()
        }),
    isPreOrderDays: yup
        .boolean(),
    preOrderDays: yup
        .number()
        .when('isPreOrderDays', {
            is: true,
            then: schema => schema.required('form_error_validate.required').min(1, 'form_error_validate.min_1'),
            otherwise: schema => schema.notRequired()
        }),
    discountType: yup
        .string()
        .oneOf([DISCOUNT_TYPE.PERCENT, DISCOUNT_TYPE.FIXED])
        .required('form_error_validate.required'),
    discountRate: yup
        .string()
        .when('discountType', {
            is: DISCOUNT_TYPE.PERCENT,
            then: schema => schema.required('form_error_validate.required').min(1).max(DISCOUNT_RATE),
            otherwise: schema => schema.notRequired()
        }),
    maxDiscountPercent: yup.string().when('discountType', {
        is: DISCOUNT_TYPE.PERCENT,
        then: schema => schema.required('form_error_validate.required').min(1).max(MAX_PRICE_SERVICE),
        otherwise: schema => schema.notRequired()
    }),
    maxDiscountAmount: yup.string().when('discountType', {
        is: DISCOUNT_TYPE.FIXED,
        then: schema => schema.required('form_error_validate.required').min(1).max(MAX_PRICE_SERVICE),
        otherwise: schema => schema.notRequired()
    })
});

type FormData = yup.InferType<typeof validateSchema>;

export default observer(function FormPromotion({ typeForm }: FormPromotionProps) {
    const initialValues = {
        isAllShop: true,
        shopId: null,
        shopName: '',
        nameVn: '',
        nameEn: '',
        promotionCode: '',
        startTime: undefined,
        endTime: undefined,
        quantity: undefined,
        perUserLimit: undefined,
        descriptionVn: '',
        descriptionEn: '',
        status: true,
        isFirstOrder: false,
        isFirstReview: false,
        isFirstTimeUser: false,
        isTimeRange: false,
        promotionTimeRanges: [{
            fromTime: '00:00',
            toTime: '00:00'
        }],
        isProvince: false,
        provinceIds: [{
            provinceName: '',
            provinceCode: ''
        }],
        isMinTotal: false,
        minTotal: undefined,
        isPreOrderDays: false,
        preOrderDays: undefined,
        discountType: DISCOUNT_TYPE.PERCENT,
        discountRate: undefined,
        maxDiscountPercent: undefined,
        maxDiscountAmount: undefined
    };

    const { id } = useParams();
    const { t, i18n: { language } } = useTranslation();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const {
        modalStore: { showAlertModal, hideModal },
        generalStore: { administrativeUnit },
        promotionStore: { shopsSearchPromotion, getAllShopSearchPromotion, promotions, postPromotion,
            promotionDetail, getDetailPromotion, updatePromotion }
    } = useStore();
    const navigate = useNavigate();

    const watchIsAllShop = form.watch('isAllShop');
    const watchFromStartTime = form.watch('startTime');
    const watchToEndTime = form.watch('endTime');
    const watchShopName = form.watch('shopName');
    const watchPromotionTimeRanges = form.watch('promotionTimeRanges');
    const watchProvinceIds = form.watch('provinceIds');
    const watchDiscountType = form.watch('discountType');
    const watchIsFirstOrder = form.watch('isFirstOrder');
    const watchIsFirstReview = form.watch('isFirstReview');
    const watchIsFirstTimeUser = form.watch('isFirstTimeUser');
    const watchIsTimeRange = form.watch('isTimeRange');
    const watchIsProvince = form.watch('isProvince');
    const watchIsMinTotal = form.watch('isMinTotal');
    const watchIsPreOrderDays = form.watch('isPreOrderDays');

    // Tạo biến kiểm tra endTime < ngày hiện tại:
    const isEndTimePast = watchToEndTime && new Date(watchToEndTime).getTime() < new Date().setHours(0, 0, 0, 0);
    const isCheckCondition = !watchIsFirstOrder && !watchIsFirstReview && !watchIsFirstTimeUser && !watchIsTimeRange &&
        !watchIsProvince && !watchIsMinTotal && !watchIsPreOrderDays;
    const [isCheckConditionOnSubmit, setIsCheckConditionOnSubmit] = useState(false);

    useEffect(() => {
        if ((typeForm === FORM_TYPE.EDIT || typeForm === FORM_TYPE.CLONE) && id) {
            const loadDetail = async () => {
                const data = await flowResult(getDetailPromotion(id));
                if (data) {
                    form.reset({
                        isAllShop: typeForm === FORM_TYPE.CLONE ? true : (data?.shop?.shopId ? false : true),
                        shopId: (typeForm === FORM_TYPE.EDIT && data?.shop?.shopId) ? data?.shop?.shopId : null,
                        shopName: (typeForm === FORM_TYPE.EDIT && data?.shop?.shopId) ? data?.shop?.shopId : '',
                        nameVn: data?.nameVn ? data?.nameVn : '',
                        nameEn: data?.nameEn ? data?.nameEn : '',
                        promotionCode: (typeForm === FORM_TYPE.EDIT && data?.promotionCode) ? data?.promotionCode : '',
                        startTime: data?.startTime ? new Date(data?.startTime) : undefined,
                        endTime: data?.endTime ? new Date(data?.endTime) : undefined,
                        quantity: data?.quantity ? data?.quantity : undefined,
                        perUserLimit: data?.perUserLimit ? data?.perUserLimit : undefined,
                        descriptionVn: data?.descriptionVn ? data?.descriptionVn : '',
                        descriptionEn: data?.descriptionEn ? data?.descriptionEn : '',
                        status: data?.status ? data?.status : false,
                        isFirstOrder: data?.isFirstOrder ? data?.isFirstOrder : false,
                        isFirstReview: data?.isFirstReview ? data?.isFirstReview : false,
                        isFirstTimeUser: data?.isFirstTimeUser ? data?.isFirstTimeUser : false,
                        isTimeRange: data?.isTimeRange ? data?.isTimeRange : false,
                        promotionTimeRanges: (data?.timeRanges && data?.timeRanges?.length > 0) ?
                            data?.timeRanges :
                            [{
                                fromTime: '00:00',
                                toTime: '10:00'
                            }],
                        isProvince: data?.isProvince ? data?.isProvince : false,
                        provinceIds: getProvinceOptions(data?.provinceIds),
                        isMinTotal: data?.isMinTotal ? data?.isMinTotal : false,
                        minTotal: data?.minTotal ? data?.minTotal : undefined,
                        isPreOrderDays: data?.isPreOrderDays ? data?.isPreOrderDays : false,
                        preOrderDays: data?.preOrderDays ? data?.preOrderDays : undefined,
                        discountType: data?.discountType ? data?.discountType : DISCOUNT_TYPE.PERCENT,
                        discountRate: data?.discountRate ? data?.discountRate : undefined,
                        maxDiscountPercent: data?.maxDiscount ? data?.maxDiscount : undefined,
                        maxDiscountAmount: data?.discountValue ? data?.discountValue : undefined
                    });
                }
            };

            loadDetail();
        }
    }, []);

    const getProvinceOptions = (dataProvinces) => {
        if (dataProvinces && dataProvinces.length > 0) {
            return dataProvinces.map(item => ({
                provinceName: administrativeUnit.provinces.find(province => province.code == item)?.name || '',
                provinceCode: item
            }));
        }
        return [{
            provinceName: '',
            provinceCode: ''
        }];
    };

    useEffect(() => {
        loadData(watchShopName || '');
    }, []);

    const loadData = async (name: string) => {
        await flowResult(getAllShopSearchPromotion({ name }));
    };

    const onsubmit = async (data: FormData) => {
        let hasError = false;
        if (language === LANGUAGES_SUPPORTED.English && (!data.nameEn || data.nameEn.trim() === '')) {
            form.setError('nameEn', { type: 'manual', message: t('form_error_validate.required') });
            hasError = true;
            scrollToTop();
        }
        if (language === LANGUAGES_SUPPORTED.Vietnamese && (!data.nameVn || data.nameVn.trim() === '')) {
            form.setError('nameVn', { type: 'manual', message: t('form_error_validate.required') });
            hasError = true;
            scrollToTop();
        }

        if (data.isProvince && data.provinceIds && data.provinceIds.length > 0) {
            const codes = data.provinceIds.map(item => item.provinceCode);
            const duplicateCode = codes.find((code, idx) => code && codes.indexOf(code) !== idx);
            const checkRequiredProvice = data.provinceIds.find(item => item.provinceCode === '');

            if ((duplicateCode || checkRequiredProvice) && data.isProvince) {
                showAlertModal({
                    type: 'error',
                    content: t('messages.error_duplicated_province')
                });
                hasError = true;
            }
        }

        if (data.isTimeRange && data.promotionTimeRanges && data.promotionTimeRanges.length > 0) {
            const checkTime = data.promotionTimeRanges.find(item => item.fromTime >= item.toTime);

            if (checkTime && data.isTimeRange) {
                showAlertModal({
                    type: 'error',
                    content: t('messages.error_invalid_time')
                });
                hasError = true;
            }
        }

        if (!watchIsFirstOrder && !watchIsFirstReview && !watchIsFirstTimeUser && !watchIsTimeRange &&
            !watchIsProvince && !watchIsMinTotal && !watchIsPreOrderDays) {
            hasError = true;
            setIsCheckConditionOnSubmit(true);
            scrollToTop();
        }

        if (hasError) return;

        if (typeForm === FORM_TYPE.CREATE || typeForm === FORM_TYPE.CLONE) {
            const payload = {
                shopId: (data?.shopId && !data?.isAllShop) ? data?.shopId : null,
                nameVn: data?.nameVn,
                nameEn: data?.nameEn,
                promotionCode: data?.promotionCode,
                startTime: data?.startTime,
                endTime: data?.endTime,
                quantity: data?.quantity,
                perUserLimit: data?.perUserLimit,
                descriptionVn: data?.descriptionVn,
                descriptionEn: data?.descriptionEn,
                status: data?.status ? data?.status : false,
                isFirstOrder: data?.isFirstOrder,
                isFirstReview: data?.isFirstReview,
                isFirstTimeUser: data?.isAllShop ? data?.isFirstTimeUser : false,
                isTimeRange: data?.isTimeRange,
                promotionTimeRanges: data?.isTimeRange ? data?.promotionTimeRanges : [],
                isProvince: data?.isAllShop ? data?.isProvince : false,
                provinceIds: (data?.isProvince && data?.isAllShop && data?.provinceIds && data?.provinceIds.length > 0) ? data?.provinceIds.map(item => item?.provinceCode) : [],
                isMinTotal: data?.isMinTotal,
                minTotal: data?.isMinTotal ? data?.minTotal : null,
                isPreOrderDays: data?.isPreOrderDays,
                preOrderDays: data?.isPreOrderDays ? data?.preOrderDays : null,
                discountType: data?.discountType,
                discountRate: data?.discountType === DISCOUNT_TYPE.PERCENT ? data?.discountRate : null,
                maxDiscount: data?.discountType === DISCOUNT_TYPE.PERCENT ? data?.maxDiscountPercent : null,
                discountValue: data?.discountType === DISCOUNT_TYPE.FIXED ? data?.maxDiscountAmount : null
            };
            const res = await flowResult(postPromotion(payload));
            if (res) {
                toastify('success', t('messages.add_success'));
                navigate(ROUTES.promotionManagement.href);
            }
        } else if (typeForm === FORM_TYPE.EDIT && id) {
            const payload = {
                shopId: data?.shopId ? data?.shopId : null,
                nameVn: data?.nameVn,
                nameEn: data?.nameEn,
                startTime: data?.startTime,
                endTime: data?.endTime,
                quantity: data?.quantity,
                perUserLimit: data?.perUserLimit,
                descriptionVn: data?.descriptionVn,
                descriptionEn: data?.descriptionEn,
                status: data?.status ? data?.status : false
            };
            const res = await flowResult(updatePromotion(id, payload));
            if (res) {
                toastify('success', t('messages.add_success'));
                navigate(ROUTES.promotionManagement.href);
            }
        }
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    const handleAutoGenerateCode = () => {
        // Lấy danh sách mã đã có (giả sử mỗi promotion có trường code hoặc promotionCode)
        const existingCodes = promotions?.map((item: any) => (item.promotionCode || item.code || '')?.toUpperCase()) || [];

        // Hàm sinh mã ngẫu nhiên
        const generateCode = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let result = '';
            for (let i = 0; i < 15; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        };

        // Lặp cho đến khi ra mã không trùng
        let newCode = '';
        do {
            newCode = generateCode();
        } while (existingCodes.includes(newCode));

        // Set vào form
        form.setValue('promotionCode', newCode, { shouldValidate: true });
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onsubmit)} className='pb-5'>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-12 items-start gap-x-2 mb-4'>
                            <Label className='col-span-2 label-1 flex items-center'>
                                {t('words_title.shop_name')}
                                <span className='text-destructive ml-1'>*</span>
                            </Label>
                            {/* Radio group */}
                            <div className='col-span-10'>
                                <div className='col-span-8 flex items-center gap-6 mb-2'>
                                    <RadioGroup
                                        className='flex items-center gap-6'
                                        value={watchIsAllShop ? 'true' : 'false'}
                                        onValueChange={value => form.setValue('isAllShop', value === 'true')}
                                        disabled={typeForm === FORM_TYPE.EDIT}
                                    >
                                        <div className='flex items-center gap-1'>
                                            <RadioGroupItem id='radio-all-shop' value='true' />
                                            <Label htmlFor='radio-all-shop' className='!my-0 cursor-pointer flex '>
                                                {t('words_title.for_all_shops_form')}
                                                &nbsp;
                                                <Tooltip content={t('words_title.for_all_shops_form_alt')}>
                                                    <span tabIndex={-1} onClick={e => e.preventDefault()}>
                                                        <ShieldQuestionIcon />
                                                    </span>
                                                </Tooltip>
                                            </Label>
                                        </div>
                                        <div className='flex items-center gap-1'>
                                            <RadioGroupItem id='radio-one-shop' value='false' />
                                            <Label htmlFor='radio-one-shop' className='!my-0 cursor-pointer flex'>
                                                {t('words_title.for_a_specified_shop')}
                                                &nbsp;
                                                <Tooltip content={t('words_title.for_a_specified_shop_alt')}>
                                                    <span tabIndex={-1} onClick={e => e.preventDefault()}>
                                                        <ShieldQuestionIcon />
                                                    </span>
                                                </Tooltip>
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                                {/* Combobox */}
                                {
                                    !watchIsAllShop && (
                                        <div className='col-span-6'>
                                            <FormField
                                                control={form.control}
                                                name='shopName'
                                                render={({ field }) => (
                                                    <FormItem className='w-full'>
                                                        <FormControl>
                                                            <Combobox
                                                                placeholder={t('placeholder.choose_promotion_shop_form')}
                                                                value={field.value ?? ''}
                                                                disabled={watchIsAllShop || typeForm === FORM_TYPE.EDIT}
                                                                onChange={(item) => {
                                                                    field.onChange(item?.value);
                                                                    form.setValue('shopId', item?.code);
                                                                }}
                                                                items={
                                                                    Array.isArray(shopsSearchPromotion) ?
                                                                        shopsSearchPromotion.map(item => ({
                                                                            label: item?.name,
                                                                            value: item?.id,
                                                                            code: item?.id
                                                                        })) :
                                                                        []
                                                                }
                                                                side='top'
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                        </div>
                                    )
                                }
                            </div>
                        </div>

                        {/* Promo name (VN) */}
                        <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='nameVn'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/6 label-1' required={language === LANGUAGES_SUPPORTED.Vietnamese}>
                                                {t('words_title.promotion_name_lable')}
                                                &nbsp;
                                                <span className='text-[#777777]'>(VN)</span>
                                            </Label>
                                            <FormControl className='w-5/6'>
                                                <Input type='text' {...field} maxLength={100} />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[17%]' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Promo name (EN) */}
                        <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='nameEn'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/6 label-1' required={language === LANGUAGES_SUPPORTED.English}>
                                                {t('words_title.promotion_name_lable')}
                                                &nbsp;
                                                <span className='text-[#777777]'>(EN)</span>
                                            </Label>
                                            <FormControl className='w-5/6'>
                                                <Input type='text' {...field} maxLength={100} />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[17%]' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-y-4 mb-4'>
                            {/* Promo code */}
                            <FormField
                                control={form.control}
                                name='promotionCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/3 label-1' required>{t('words_title.promo_code_lable')}</Label>
                                            <FormControl className='w-2/3'>
                                                <Input
                                                    type='text'
                                                    {...field}
                                                    onChange={e => field.onChange(e.target.value.toUpperCase())}
                                                    disabled={typeForm === FORM_TYPE.EDIT}
                                                    maxLength={15}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[34%]' />
                                        <div className='flex flex-row-reverse mt-1'>
                                            {typeForm !== FORM_TYPE.EDIT && (
                                                <div className='text-[#0095FF] cursor-pointer' onClick={() => handleAutoGenerateCode()}>{t('words_title.auto_generate_code')}</div>
                                            )}
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {/* Validity period */}
                            <div className='flex items-start pl-12'>
                                <Label className='w-1/5 label-1 h-[48px] flex items-center pr-1' required>
                                    {t('words_title.validity_period')}
                                    <Tooltip content={t('words_title.validity_period_form_alt')}>
                                        <span tabIndex={-1} onClick={e => e.preventDefault()}>
                                            <ShieldQuestionIcon />
                                        </span>
                                    </Tooltip>
                                </Label>
                                <div className='flex items-start gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='startTime'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchToEndTime ?? undefined, false)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from_promotion')}
                                                        required
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className='mt-3'> - </div>
                                    <FormField
                                        control={form.control}
                                        name='endTime'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchFromStartTime ?? undefined, true)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to_promotion')}
                                                        required
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                        </div>

                        {/* Number of voucher */}
                        <div className='grid grid-cols-2 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='quantity'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/3 label-1' required>{t('words_title.quantity_voucher_form')}</Label>
                                            <FormControl className='w-2/3'>
                                                <FormatNumber
                                                    minValue={1}
                                                    maxValue={NUMBER_OF_VOUCHER}
                                                    decimalScale={0}
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    setvalueOfNumericFormat={(values) => {
                                                        if (values === '' || values === null) {
                                                            field.onChange(null);
                                                        } else {
                                                            field.onChange(Number(values));
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[34%]' />
                                    </FormItem>
                                )}
                            />

                            {/* Usage limit */}
                            <div className='pl-12'>
                                <FormField
                                    control={form.control}
                                    name='perUserLimit'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex items-center relative'>
                                                <Label className='w-1/5 label-1' required>{t('words_title.limits_of_use')}</Label>
                                                <FormControl className='w-4/5'>
                                                    <FormatNumber
                                                        minValue={1}
                                                        maxValue={USAGE_LIMIT_VOUCHER}
                                                        decimalScale={0}
                                                        statusDisplay='input'
                                                        thousandSeparator={true}
                                                        allowNegative={false}
                                                        value={field.value}
                                                        setvalueOfNumericFormat={(values) => {
                                                            if (values === '' || values === null) {
                                                                field.onChange(null);
                                                            } else {
                                                                field.onChange(Number(values));
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                                <div className='absolute right-[14px] top-[12px] z-50 text-[#777777]'>
                                                    {t('placeholder.usage_limit')}
                                                </div>
                                            </div>
                                            <FormMessage className='ml-[21%]' />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className='grid grid-cols-2 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='descriptionVn'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/3 label-1 self-start pt-1'>
                                                {t('words_title.description_input')}
                                                {' '}
                                                &nbsp;
                                                <span className='text-[#777777]'>(VN)</span>
                                            </Label>
                                            <FormControl className='w-2/3'>
                                                <Textarea
                                                    className='mt-[0.375rem] resize-none h-[133px]'
                                                    value={field.value ?? ''}
                                                    maxLength={1000}
                                                    onChange={e => field.onChange(e.target.value)}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className='pl-12'>
                                <FormField
                                    control={form.control}
                                    name='descriptionEn'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex items-center'>
                                                <Label className='w-1/5 label-1 self-start pt-1'>
                                                    {t('words_title.description_input')}
                                                    {' '}
                                                    &nbsp;
                                                    <span className='text-[#777777]'>(EN)</span>
                                                </Label>
                                                <FormControl className='w-4/5'>
                                                    <Textarea
                                                        className='mt-[0.375rem] resize-none h-[133px]'
                                                        value={field.value ?? ''}
                                                        maxLength={1000}
                                                        onChange={e => field.onChange(e.target.value)}
                                                    />
                                                </FormControl>
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <FormField
                            control={form.control}
                            name='status'
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex items-center gap-4 pb-4'>
                                        <Label className='label-1 w-1/6'>{t('words_title.status')}</Label>
                                        <FormControl className='w-5/6'>
                                            <RadioGroup
                                                className='flex items-center'
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                }}
                                                value={field.value.toString()}
                                                disabled={!!isEndTimePast}
                                            >
                                                <FormItem className='flex w-44 items-center gap-2'>
                                                    <FormControl>
                                                        <RadioGroupItem id='trueRadioStatus' value='true' />
                                                    </FormControl>
                                                    <Label
                                                        className='!my-0 cursor-pointer'
                                                        htmlFor='trueRadioStatus'
                                                    >
                                                        {t('words_title.shop_status_active')}
                                                    </Label>
                                                </FormItem>
                                                <FormItem className='flex w-44 items-center gap-2'>
                                                    <FormControl>
                                                        <RadioGroupItem id='falseRadioStatus' value='false' />
                                                    </FormControl>
                                                    <Label
                                                        className='!my-0 cursor-pointer'
                                                        htmlFor='falseRadioStatus'
                                                    >
                                                        {t('words_title.user_status_inactive_promotion')}
                                                    </Label>
                                                </FormItem>
                                            </RadioGroup>
                                        </FormControl>
                                    </div>
                                    <div className='flex items-center gap-4'>
                                        <div className='w-1/6'></div>
                                        <FormMessage className='w-5/6' />
                                    </div>
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Promotion conditions (required) */}
                    <div className='bg-white rounded p-4 mt-3'>
                        <div className='mb-4'>
                            <div className={`text-[16px] font-bold p-3 bg-[#F2F2F2] rounded border-[1px] ${(isCheckCondition && isCheckConditionOnSubmit) ? 'border-[#ef4444]' : ''}`}>
                                {t('words_title.promotion_promotion_conditions')}
                                <span className='text-[#646464] text-[14px] font-normal ml-2'>{t('words_title.promotion_required')}</span>
                            </div>
                            {
                                isCheckCondition && isCheckConditionOnSubmit && (
                                    <span className='text-sm font-medium text-destructive whitespace-pre-line'>
                                        {t('form_error_validate.must_one_form_of_promotion')}
                                    </span>
                                )
                            }
                        </div>

                        {/* Condition 1: For the first appointment only */}
                        <div className={`border-dashed border-[1px] rounded-[8px] p-4 mb-4 ${watchIsFirstOrder ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'} `}>
                            <FormField
                                control={form.control}
                                name='isFirstOrder'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex gap-2 items-center'>
                                            <FormControl>
                                                <Checkbox
                                                    id='isFirstOrder'
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={typeForm === FORM_TYPE.EDIT}
                                                />
                                            </FormControl>
                                            <Label htmlFor='isFirstOrder'>
                                                <div className='flex cursor-pointer'>
                                                    <div className='font-semibold mr-2'>
                                                        {t('words_title.condition_1')}
                                                    </div>
                                                    <div>
                                                        {t('words_title.condition_1_lable')}
                                                    </div>
                                                </div>
                                            </Label>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Condition 2: For the fisrt review */}
                        <div className={`border-dashed border-[1px] rounded-[8px] p-4 mb-4 ${watchIsFirstReview ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                            <FormField
                                control={form.control}
                                name='isFirstReview'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex gap-2 items-center'>
                                            <FormControl>
                                                <Checkbox
                                                    id='isFirstReview'
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={typeForm === FORM_TYPE.EDIT}
                                                />
                                            </FormControl>
                                            <Label htmlFor='isFirstReview'>
                                                <div className='flex cursor-pointer'>
                                                    <div className='font-semibold mr-2'>
                                                        {t('words_title.condition_2')}
                                                    </div>
                                                    <div>
                                                        {t('words_title.condition_2_lable')}
                                                    </div>
                                                </div>
                                            </Label>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Condition 3: For the first login */}
                        {watchIsAllShop && (
                            <div className={`border-dashed border-[1px] rounded-[8px] p-4 mb-4 ${watchIsFirstTimeUser ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                                <FormField
                                    control={form.control}
                                    name='isFirstTimeUser'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex gap-2 items-center'>
                                                <FormControl>
                                                    <Checkbox
                                                        id='isFirstTimeUser'
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        disabled={typeForm === FORM_TYPE.EDIT}
                                                    />
                                                </FormControl>
                                                <Label htmlFor='isFirstTimeUser'>
                                                    <div className='flex cursor-pointer'>
                                                        <div className='font-semibold mr-2'>
                                                            {t('words_title.condition_3')}
                                                        </div>
                                                        <div>
                                                            {t('words_title.condition_3_lable')}
                                                        </div>
                                                    </div>
                                                </Label>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}

                        {/* Condition 4: Limited to specific booking time */}
                        <div className={`border-dashed  border-[1px] rounded-[8px] p-4 mb-4 flex ${watchIsTimeRange ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                            <div className={`pr-2 border-[#E8E8E8] w-[500px] ${watchIsTimeRange ? 'border-r-[1px] pt-4' : ''} `}>
                                <FormField
                                    control={form.control}
                                    name='isTimeRange'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex gap-2 items-center'>
                                                <FormControl>
                                                    <Checkbox
                                                        id='isTimeRange'
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        disabled={typeForm === FORM_TYPE.EDIT}
                                                    />
                                                </FormControl>
                                                <Label htmlFor='isTimeRange'>
                                                    <div className='flex cursor-pointer'>
                                                        <div className='font-semibold mr-2'>
                                                            {t('words_title.condition_4')}
                                                        </div>
                                                        <div>
                                                            {t('words_title.condition_4_lable')}
                                                        </div>
                                                    </div>
                                                </Label>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div>
                                {
                                    watchIsTimeRange && watchPromotionTimeRanges && watchPromotionTimeRanges.length > 0 && watchPromotionTimeRanges.map((item, index) => {
                                        return (
                                            <ConditionBookingTimeItem key={index} index={index} form={form} total={watchPromotionTimeRanges.length} isEdit={typeForm === FORM_TYPE.EDIT} />
                                        );
                                    })
                                }
                            </div>
                        </div>

                        {/* Condition 5: Limited to specific cities */}
                        {watchIsAllShop && (
                            <div className={`border-dashed  border-[1px] rounded-[8px] p-4 mb-4 flex ${watchIsProvince ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                                <div className={`pr-2 border-[#E8E8E8] w-[450px] ${watchIsProvince ? 'border-r-[1px] pt-4' : ''} `}>
                                    <FormField
                                        control={form.control}
                                        name='isProvince'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex gap-2 items-center'>
                                                    <FormControl>
                                                        <Checkbox
                                                            id='isProvince'
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                            disabled={typeForm === FORM_TYPE.EDIT}
                                                        />
                                                    </FormControl>
                                                    <Label htmlFor='isProvince'>
                                                        <div className='flex cursor-pointer'>
                                                            <div className='font-semibold mr-2'>
                                                                {t('words_title.condition_5')}
                                                            </div>
                                                            <div>
                                                                {t('words_title.condition_5_lable')}
                                                            </div>
                                                        </div>
                                                    </Label>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div>
                                    {
                                        watchIsProvince && watchProvinceIds && watchProvinceIds.length > 0 && watchProvinceIds.map((item, index) => {
                                            return (
                                                <ProvinceIdItem key={index} index={index} form={form} total={watchProvinceIds.length} isEdit={typeForm === FORM_TYPE.EDIT} />
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        )}

                        {/* Condition 6: Limited to Total Amount */}
                        <div className={`border-dashed border-[1px] rounded-[8px] p-4 mb-4 flex ${watchIsMinTotal ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                            <div className={`pr-2 border-[#E8E8E8] w-[450px] ${watchIsMinTotal ? 'border-r-[1px] pt-4' : ''} `}>
                                <FormField
                                    control={form.control}
                                    name='isMinTotal'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex gap-2 items-center'>
                                                <FormControl>
                                                    <Checkbox
                                                        id='isMinTotal'
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        disabled={typeForm === FORM_TYPE.EDIT}
                                                    />
                                                </FormControl>
                                                <Label htmlFor='isMinTotal'>
                                                    <div className='flex cursor-pointer'>
                                                        <div className='font-semibold mr-2'>
                                                            {t('words_title.condition_6')}
                                                        </div>
                                                        <div>
                                                            {t('words_title.condition_6_lable')}
                                                        </div>
                                                    </div>
                                                </Label>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {
                                watchIsMinTotal && (
                                    <div className='pl-[54px] flex items-center'>
                                        <FormField
                                            control={form.control}
                                            name='minTotal'
                                            render={({ field }) => (
                                                <FormItem className=' w-full'>
                                                    <FormControl>
                                                        <FormatNumber
                                                            placeholder={t('words_title.promotion_select_dk6')}
                                                            minValue={1}
                                                            maxValue={MAX_PRICE_SERVICE}
                                                            decimalScale={0}
                                                            className='w-[350px] placeholder:font-normal'
                                                            statusDisplay='input'
                                                            thousandSeparator={true}
                                                            allowNegative={false}
                                                            value={field.value}
                                                            disabled={typeForm === FORM_TYPE.EDIT}
                                                            setvalueOfNumericFormat={(values) => {
                                                                if (values === '' || values === null) {
                                                                    field.onChange(null);
                                                                } else {
                                                                    field.onChange(Number(values));
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <div className='ml-3'>VND</div>
                                    </div>
                                )
                            }
                        </div>

                        {/* Condition 7: Book {x} days in advance */}
                        <div className={`border-dashed border-[1px] rounded-[8px] p-4 mb-4 flex ${watchIsPreOrderDays ? 'border-[#B23B3B]' : 'border-[#E8E8E8]'}`}>
                            <div className={`pr-2 border-[#E8E8E8] w-[450px] ${watchIsPreOrderDays ? 'border-r-[1px] pt-4' : ''} `}>
                                <FormField
                                    control={form.control}
                                    name='isPreOrderDays'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex gap-2 items-center'>
                                                <FormControl>
                                                    <Checkbox
                                                        id='isPreOrderDays'
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        disabled={typeForm === FORM_TYPE.EDIT}
                                                    />
                                                </FormControl>
                                                <Label htmlFor='isPreOrderDays'>
                                                    <div className='flex cursor-pointer'>
                                                        <div className='font-semibold mr-2'>
                                                            {t('words_title.condition_7')}
                                                        </div>
                                                        <div>
                                                            {t('words_title.condition_7_lable')}
                                                        </div>
                                                    </div>
                                                </Label>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {
                                watchIsPreOrderDays && (
                                    <div className='pl-[54px] flex items-start'>
                                        <FormField
                                            control={form.control}
                                            name='preOrderDays'
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col w-full'>
                                                    <FormControl>
                                                        <FormatNumber
                                                            minValue={1}
                                                            placeholder={t('words_title.promotion_select_dk7')}
                                                            maxValue={BOOK_DAY_IN_ADVANCE}
                                                            decimalScale={0}
                                                            className='w-[350px] placeholder:font-normal'
                                                            statusDisplay='input'
                                                            thousandSeparator={true}
                                                            allowNegative={false}
                                                            value={field.value}
                                                            disabled={typeForm === FORM_TYPE.EDIT}
                                                            setvalueOfNumericFormat={(values) => {
                                                                if (values === '' || values === null) {
                                                                    field.onChange(null);
                                                                } else {
                                                                    field.onChange(Number(values));
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <div className='ml-3 mt-[14px]'>{t('words_title.promotion_select_dk7_day')}</div>
                                    </div>
                                )
                            }
                        </div>
                    </div>

                    {/* Promotion method (required) */}
                    <div className='bg-white rounded p-4 mt-3'>
                        <div className='text-[16px] font-bold p-3 bg-[#F2F2F2] mb-4 rounded flex items-center'>
                            {t('words_title.promotion_promotion_method')}
                            <span className='text-[#646464] text-[14px] font-normal ml-2'>{t('words_title.promotion_required')}</span>
                        </div>
                        <RadioGroup
                            className='flex flex-col gap-3'
                            value={watchDiscountType}
                            onValueChange={value => form.setValue('discountType', value)}
                            disabled={typeForm === FORM_TYPE.EDIT}
                        >
                            {/* Percent */}
                            <div className={`flex items-start gap-2 p-4 rounded border border-dashed ${watchDiscountType === DISCOUNT_TYPE.PERCENT ? 'border-[#B23B3B] bg-[#FFF8F7]' : 'border-[#E8E8E8] bg-white'}`}>
                                <div className='flex items-center mt-[14px]'>
                                    <RadioGroupItem id='radio-percent' value={DISCOUNT_TYPE.PERCENT} />
                                    <Label htmlFor='radio-percent' className='!my-0 cursor-pointer flex font-semibold ml-2'>
                                        {t('words_title.promotion_method_discount')}
                                    </Label>
                                </div>
                                <FormField
                                    control={form.control}
                                    name='discountRate'
                                    render={({ field }) => (
                                        <FormItem className='mx-2'>
                                            <FormControl>
                                                <FormatNumber
                                                    minValue={1}
                                                    maxValue={DISCOUNT_RATE}
                                                    decimalScale={0}
                                                    className='w-[200px]'
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    disabled={watchDiscountType !== DISCOUNT_TYPE.PERCENT || typeForm === FORM_TYPE.EDIT}
                                                    setvalueOfNumericFormat={(values) => {
                                                        if (values === '' || values === null) {
                                                            field.onChange(null);
                                                        } else {
                                                            field.onChange(Number(values));
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Label htmlFor='radio-percent' className='flex items-center mt-[14px] cursor-pointer'>
                                    <span className='font-semibold'>%</span>
                                    <span className='ml-2'>{t('words_title.promotion_method_percent_lable')}</span>
                                </Label>
                                <FormField
                                    control={form.control}
                                    name='maxDiscountPercent'
                                    render={({ field }) => (
                                        <FormItem className='mx-2'>
                                            <FormControl>
                                                <FormatNumber
                                                    minValue={1}
                                                    maxValue={MAX_PRICE_SERVICE}
                                                    decimalScale={0}
                                                    className='w-[200px]'
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    disabled={watchDiscountType !== DISCOUNT_TYPE.PERCENT || typeForm === FORM_TYPE.EDIT}
                                                    setvalueOfNumericFormat={(values) => {
                                                        if (values === '' || values === null) {
                                                            field.onChange(null);
                                                        } else {
                                                            field.onChange(Number(values));
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Label htmlFor='radio-percent' className='mt-[14px] cursor-pointer'>
                                    <span className='font-semibold'>VND</span>
                                </Label>
                            </div>
                            {/* Amount */}
                            <div className={`flex items-start gap-2 p-4 rounded border border-dashed ${watchDiscountType === DISCOUNT_TYPE.FIXED ? 'border-[#B23B3B] bg-[#FFF8F7]' : 'border-[#E8E8E8] bg-white'}`}>
                                <div className='flex items-center mt-[14px]'>
                                    <RadioGroupItem id='radio-amount' value={DISCOUNT_TYPE.FIXED} />
                                    <Label htmlFor='radio-amount' className='!my-0 cursor-pointer flex font-semibold ml-2'>
                                        {t('words_title.promotion_method_discount')}
                                    </Label>
                                </div>

                                <FormField
                                    control={form.control}
                                    name='maxDiscountAmount'
                                    render={({ field }) => (
                                        <FormItem className='mx-2'>
                                            <FormControl>
                                                <FormatNumber
                                                    minValue={1}
                                                    maxValue={MAX_PRICE_SERVICE}
                                                    decimalScale={0}
                                                    className='w-[200px]'
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    disabled={watchDiscountType !== DISCOUNT_TYPE.FIXED || typeForm === FORM_TYPE.EDIT}
                                                    setvalueOfNumericFormat={(values) => {
                                                        if (values === '' || values === null) {
                                                            field.onChange(null);
                                                        } else {
                                                            field.onChange(Number(values));
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Label htmlFor='radio-amount' className='flex items-center mt-[14px] cursor-pointer'>
                                    <span className='font-semibold'>VND</span>
                                    <span className='ml-2'>{t('words_title.promotion_method_amount_lable')}</span>
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <div className='bg-white rounded p-4 mt-3'>
                        <div className='flex justify-center'>
                            <button
                                type='button'
                                className='bg-[#EBEBEB] text-[#000] cursor-pointer w-[200px] h-[40px] leading-[40px] text-center rounded-sm mr-[30px]'
                                onClick={() => {
                                    navigate(ROUTES.promotionManagement.href);
                                }}
                            >
                                {t('words_title.cancel')}
                            </button>

                            <button type='submit' className='bg-[#9A6051] text-[#fff] cursor-pointer w-[200px] h-[40px] leading-[40px] text-center rounded-sm'>
                                {t('words_title.save')}
                            </button>
                        </div>
                    </div>

                </form>
            </Form>
        </>
    );
});

interface ConditionBookingTimeItemProps {
    form: UseFormReturn<FormData>,
    index: number,
    total: number,
    isEdit: boolean
}

const ConditionBookingTimeItem = ({ form, index, total, isEdit = false }: ConditionBookingTimeItemProps) => {
    const { t } = useTranslation();

    // Thêm 1 phần tử mới
    const handleAdd = () => {
        const current = form.getValues('promotionTimeRanges') || [];
        form.setValue('promotionTimeRanges', [
            ...current,
            { fromTime: '00:00', toTime: '00:00' }
        ]);
    };

    // Xóa phần tử theo index
    const handleRemove = () => {
        const current = form.getValues('promotionTimeRanges') || [];
        if (current.length > 1) {
            const newArr = current.filter((_, i) => i !== index);
            form.setValue('promotionTimeRanges', newArr);
        }
    };

    return (
        <>
            <div className='flex items-center'>
                <div className='min-w-[40px] mx-2'>
                    {
                        index !== 0 && (
                            <div className='text-center'>{t('words_title.condition_4_or')}</div>
                        )
                    }
                </div>
                <StepTimeSelect className='' title='' name={`promotionTimeRanges.${index}.fromTime`} control={form.control} disabled={isEdit} />
                <div className='mx-4'> ~ </div>
                <StepTimeSelect className='' title='' name={`promotionTimeRanges.${index}.toTime`} control={form.control} disabled={isEdit} />
                {
                    !(index === 0 && total === 1) && !isEdit && (
                        <div className='ml-4 cursor-pointer' onClick={handleRemove}><CircleMinus /></div>
                    )
                }
                {
                    index === (total - 1) && !isEdit && (
                        <div className='ml-4 cursor-pointer' onClick={handleAdd}><CirclePlus /></div>
                    )
                }
            </div>
        </>
    );
};

interface ProvinceIdItemProps {
    form: UseFormReturn<FormData>,
    index: number,
    total: number,
    isEdit: boolean
}

const ProvinceIdItem = ({ form, index, total, isEdit = false }: ProvinceIdItemProps) => {
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        generalStore: { administrativeUnit },
        modalStore: { showModal }
    } = useStore();

    // Thêm 1 phần tử mới
    const handleAdd = () => {
        const current = form.getValues('provinceIds') || [];
        form.setValue('provinceIds', [
            ...current,
            { provinceName: '', provinceCode: '' }
        ]);
    };

    // Xóa phần tử theo index
    const handleRemove = () => {
        const current = form.getValues('provinceIds') || [];
        if (current.length > 1) {
            const newArr = current.filter((_, i) => i !== index);
            form.setValue('provinceIds', newArr);
        }
    };

    return (
        <>
            <div className='flex items-center mb-1'>
                <div className='min-w-[40px] mx-2'>
                    {
                        index !== 0 && (
                            <div className='text-center'>{t('words_title.condition_4_or')}</div>
                        )
                    }
                </div>
                <FormField
                    control={form.control}
                    name={`provinceIds.${index}.provinceName`}
                    render={({ field }) => (
                        <FormItem>
                            <div className='flex items-center'>
                                <FormControl className='w-[350px]'>
                                    <Combobox
                                        placeholder={t('words_title.promotion_select_dk5')}
                                        value={field.value ?? ''}
                                        disabled={isEdit}
                                        onChange={(item) => {
                                            field.onChange(item?.value);
                                            form.setValue(`provinceIds.${index}.provinceCode`, item?.code);
                                        }}
                                        items={
                                            (Array.isArray(administrativeUnit?.provinces) ?
                                                [
                                                    ...administrativeUnit.provinces.map(item => ({
                                                        label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                        value: item.name,
                                                        code: item.code
                                                    }))] :
                                                []
                                            )
                                        }
                                        side='top'
                                    />
                                </FormControl>
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {
                    !(index === 0 && total === 1) && !isEdit && (
                        <div className='ml-4 cursor-pointer' onClick={handleRemove}><CircleMinus /></div>
                    )
                }
                {
                    index === (total - 1) && !isEdit && (
                        <div className='ml-4 cursor-pointer' onClick={handleAdd}><CirclePlus /></div>
                    )
                }
            </div>
        </>
    );
};
